import _ from 'lodash'
import React, { useEffect } from 'react'

import { CSSTransition, TransitionGroup } from 'react-transition-group'
import { Header, Segment } from 'semantic-ui-react'
import 'swiper/components/navigation/navigation.min.css'
import 'swiper/components/pagination/pagination.min.css'

import SwiperCore, { Pagination } from 'swiper/core'

import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/swiper.min.css'
import { MatchProps } from '../../common/slice'
import { useAppDispatch } from '../../common/store'
import { replaceXWithSpace } from '../../common/util'
import {
    PLANNING_SUB_PATH,
    RECAPTCHA_ENABLED,
    WARNING_NO_COMPARABLE_PLANNING_PROPERTY_MESSAGE,
    WARNING_NO_COMPARABLE_PROPERTY_HEADER,
} from '../../environment'

import SchemeInformation from '../scheme-information/SchemeInformation'
import VctRecaptcha from '../vct-recaptcha/VctRecaptcha'
import NoComparablePropertyWarning from '../warning/NoComparablePropertyWarning'
import PlanningApplications from './PlanningApplications'

import './PlanningMainComponent.css'
import PlanningSearchBar from './PlanningSearchBar'
import { selectPlanningParcel } from './slice'
import { usePlanningData } from './util'

// install Swiper modules
SwiperCore.use([Pagination])

function PlanningMainComponent({ match }: MatchProps) {
  const dispatch = useAppDispatch()
  const planningData = usePlanningData()
  const laisKey = match?.params?.laisKey
  const parcelPlanningItem = planningData?.parcelPlanningItem
  const parcelNeighbourItems = planningData?.parcelNeighbourItems

  useEffect(() => {
    /**
     * If the user has supplied a path on the URL, attempt to
     * retrieve valuation data from the laisKey.
     *
     */
    if (laisKey && (_.isEmpty(planningData))) {
      dispatch(selectPlanningParcel({ LAISKEY: replaceXWithSpace(laisKey) }))
    }
  }, [match])

  /**
   * Returns a key to identify this component. This is used by the react CSSTransition to compare keys when re-rendering the component.
   *
   * @returns {string|*}
   */
  function getPlanningDataKey() {
    if (_.isEmpty(planningData) || !planningData?.parcelPlanningItem) {
      return 'empty'
    } else {
      return parcelPlanningItem?.address
    }
  }

  const renderPlanningDetails = () => {
    return (
      <TransitionGroup className={'planningDetailsTransitionContainer'}>
        <CSSTransition
          key={getPlanningDataKey()}
          appear={true}
          in={true}
          timeout={1000}
          classNames="parcel-details">
          <div className={'planningDetailsContainer'}>
            <div className={'planningDetails'}>
              {_.isEmpty(parcelNeighbourItems) &&
                  <NoComparablePropertyWarning header={WARNING_NO_COMPARABLE_PROPERTY_HEADER}
                                               content={WARNING_NO_COMPARABLE_PLANNING_PROPERTY_MESSAGE}/>}
              <Header as="h3" attached className={'planningDetailsHeader'}>
                {parcelPlanningItem?.address ? parcelPlanningItem.address : 'UNAVAILABLE'}
              </Header>
              <Segment attached>
                <p className={'planningDetailsLocationAndLot'}>
                  {parcelPlanningItem?.lotDetails ? parcelPlanningItem.lotDetails : 'UNAVAILABLE'}
                </p>
              </Segment>
              <Segment attached className={'planningDetailsSegment'}>
                <Swiper slidesPerView={1} spaceBetween={30} loop={true} autoHeight={true} pagination={{
                  el: '.planning-pagination',
                  'clickable': true,
                  'renderBullet': function (index, className) {
                    return '<span class="' + className + '">' + (index + 1) + '</span>'
                  },
                }} className="planningSwiper">
                  <SwiperSlide>
                    <div className={'planningSchemeInformation'}>
                      {parcelPlanningItem && <SchemeInformation planningData={parcelPlanningItem}/>}
                    </div>
                  </SwiperSlide>
                  <SwiperSlide>
                    <div className={'planningApps'}>
                      <PlanningApplications planningApps={parcelPlanningItem?.planningApps} labelWidth={6}
                                            valueWidth={10}/>
                    </div>
                  </SwiperSlide>
                  <div className={'planning-pagination'}></div>
                </Swiper>

              </Segment>
            </div>
          </div>
        </CSSTransition>
      </TransitionGroup>
    )
  }


  return (
    <React.Fragment>
      <PlanningSearchBar urlSubPath={PLANNING_SUB_PATH}/>
      {planningData && !_.isEmpty(planningData) && renderPlanningDetails()}
      {RECAPTCHA_ENABLED && <VctRecaptcha/>}
    </React.Fragment>
  )


}

export default (PlanningMainComponent)
